import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';

import { EDIT_EMPLOYEE_PATH } from '@app/constants';
import {
  U_EMPLOYEE,
  U_CLIENT_SERVICES,
  U_CLIENT,
  U_TIME,
  U_CREATE_USER,
  U_STATUS,
  U_KEY,
  U_CLIENT_SERVICES_COST,
  U_ACTION,
  U_TAGS,
} from '@app/constants/tables';
import { TagsOverflowList } from '@app/shared/ui/TagsOverflowList';
import type { NRecords } from '@app/types';
import { divideAmount, getStatusColor } from '@app/utils';

const { Text } = Typography;

interface Props {
  records: Array<NRecords.ClientRecord>;
  onOpenRecord(id: number): void;
  onOpenRecordNewTab(id: number): void;
}
export const adaptDataToTable = ({
  records,
  onOpenRecord,
  onOpenRecordNewTab,
}: Props) => {
  return (
    records &&
    records.map((record: NRecords.ClientRecord) => {
      const {
        Id,
        Employee,
        Position,
        ClientServices,
        Client,
        ClientPhones,
        Time,
        CreateUser,
        TimeCreated,
        Status,
        EmployeeId,
        ClientId,
        Cost,
        Tags,
      } = record;

      // Single-line, compact datetime: "пн, 21.07.2025 14:30".
      const formatISODateToCustomFormat = (isoDate: string) => {
        const date = new Date(isoDate);

        const formattedDate = date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

        const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' });

        const formattedTime = date.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        });

        return `${weekday}, ${formattedDate} ${formattedTime}`;
      };

      const recordTags = (Tags || []).map((tag) => ({
        id: tag.Id,
        label: tag.Name ?? '',
        color: tag.Color ?? undefined,
        iconId: tag.IconId ?? undefined,
      }));

      return {
        [U_KEY]: String(Id),
        [U_TIME]: (
          <Link
            to={`?recordId=${Id}`}
            className="date-button record-time-cell"
            data-testid="visit-date-time"
            onClick={(e) => {
              e.preventDefault();
              onOpenRecord(Id);
            }}
          >
            {formatISODateToCustomFormat(Time)}
          </Link>
        ),
        [U_EMPLOYEE]: (
          <div>
            <Link
              to={`${EDIT_EMPLOYEE_PATH}?id=${EmployeeId}&key=1`}
              data-testid="executor"
            >
              {Employee}
            </Link>{' '}
            <br />
            <Text type="secondary" data-testid="executor-role">
              {Position}
            </Text>
          </div>
        ),
        [U_CLIENT_SERVICES]: (
          <div className="record-services-cell" data-testid="services">
            {ClientServices?.split(';').map((service, index) => (
              <Text key={index} className="no-wrap-text">
                {service}
              </Text>
            ))}
          </div>
        ),
        [U_CLIENT_SERVICES_COST]: (
          <div
            className="service-cost record-cost-cell"
            data-testid="service-sum"
          >
            {divideAmount(+Cost)}
          </div>
        ),
        [U_CLIENT]: (
          <div>
            <Link
              to={`/clients/card/?id=${ClientId}`}
              data-testid="client-name"
            >
              {Client}
            </Link>{' '}
            <br />
            <Text>{ClientPhones}</Text>
          </div>
        ),
        [U_CREATE_USER]: (
          <div>
            <Text data-testid="created-by">{CreateUser}</Text> <br />
            <Text data-testid="creation-date-time">
              {formatISODateToCustomFormat(TimeCreated)}
            </Text>
          </div>
        ),
        [U_STATUS]: (
          <span
            className="record-status-badge"
            data-testid="visit-status"
            style={{
              color: getStatusColor(Status),
              borderColor: getStatusColor(Status),
            }}
          >
            {Status}
          </span>
        ),
        [U_TAGS]: <TagsOverflowList items={recordTags} />,
        [U_ACTION]: (
          <Tooltip title="Открыть запись в новой вкладке">
            <Button
              icon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
              onClick={() => onOpenRecordNewTab(Id)}
              data-testid="open-record-in-new-window"
            />
          </Tooltip>
        ),
      };
    })
  );
};
